import type { BadgeProps } from "@hightouch/htui";
import {
  type AmpActionStatus,
  isAmpActionStatus,
} from "@hightouch/lib/ad-studio/board/constants";
import { MarketingCampaignProjectStatuses } from "@hightouch/lib/llm/lifecycle-studio/campaigns/types";

import type { AmpProjectLinkFragment } from "src/graphql";
import { ContentEmailStatus } from "src/pages/lifecycle-studio/campaigns/detail/types";
import { isCampaignBrief } from "src/pages/projects/links";
import { abbreviateNumber } from "src/utils/numbers";

export type AttachmentBadge = {
  label: string;
  variant: NonNullable<BadgeProps["variant"]>;
  /** Set while the work runs, so the badge spins. */
  progress?: "indeterminate";
};

export type AttachmentFacts = {
  badge: AttachmentBadge | null;
  /** Short secondary facts, shown in order after the attachment's kind. */
  facts: string[];
};

const NO_FACTS: AttachmentFacts = { badge: null, facts: [] };

// A completed email needs no badge.
const EMAIL_STATUS_BADGES = new Map<string, AttachmentBadge>([
  [
    ContentEmailStatus.Generating,
    { label: "Generating", variant: "info", progress: "indeterminate" },
  ],
  [
    ContentEmailStatus.Editing,
    { label: "Updating", variant: "info", progress: "indeterminate" },
  ],
  [ContentEmailStatus.Failed, { label: "Failed", variant: "error" }],
  [ContentEmailStatus.Canceled, { label: "Canceled", variant: "ghost" }],
]);

// A Library file an agent is still building, or failed to; a ready one needs no badge.
const FILE_STATUS_BADGES = new Map<string, AttachmentBadge>([
  [
    "generating",
    { label: "Generating", variant: "info", progress: "indeterminate" },
  ],
  ["failed", { label: "Failed", variant: "error" }],
]);

const CAMPAIGN_STATUS_FACTS = new Map<string, string>([
  [MarketingCampaignProjectStatuses.BriefGenerating, "Writing the brief"],
  [MarketingCampaignProjectStatuses.BriefCompleted, "Brief ready"],
  [MarketingCampaignProjectStatuses.BriefFailed, "Brief failed"],
]);

const ACTION_STATUS_BADGES: Record<AmpActionStatus, AttachmentBadge> = {
  queued: { label: "Queued", variant: "subtle" },
  running: { label: "Running", variant: "info", progress: "indeterminate" },
  waiting_consent: { label: "Waiting for approval", variant: "warning" },
  done: { label: "Done", variant: "success" },
  failed: { label: "Failed", variant: "error" },
  canceled: { label: "Canceled", variant: "ghost" },
};

/**
 * The facts an attachment row shows, read from the record it links: an
 * audience's size, how far an agent action got.
 */
export function attachmentFacts(link: AmpProjectLinkFragment): AttachmentFacts {
  if (link.audience) {
    const size = link.audience.last_run_size;
    return {
      badge: null,
      facts:
        size === null
          ? []
          : [`${abbreviateNumber(size)} ${plural(size, "member")}`],
    };
  }
  const fileBadge = link.amp_file
    ? (FILE_STATUS_BADGES.get(link.amp_file.generation_status) ?? null)
    : null;
  const email = link.amp_file?.content_email;
  if (email) {
    return {
      // The email's own status also tells an edit apart from a first build.
      badge:
        (email.status ? EMAIL_STATUS_BADGES.get(email.status) : undefined) ??
        fileBadge,
      facts: [],
    };
  }
  if (link.marketing_campaign) {
    const status = link.marketing_campaign.project_status;
    const fact = status ? CAMPAIGN_STATUS_FACTS.get(status) : undefined;
    return { badge: null, facts: fact ? [fact] : [] };
  }
  if (isCampaignBrief(link.campaign_artifact)) {
    return { badge: null, facts: ["Lifecycle Studio brief"] };
  }
  if (link.sync) {
    const name = link.sync.destination?.name;
    return { badge: null, facts: name ? [`To ${name}`] : [] };
  }
  if (link.amp_action) {
    const { status } = link.amp_action;
    const total = link.amp_action.calls_total.aggregate?.count ?? 0;
    const waiting = link.amp_action.calls_waiting.aggregate?.count ?? 0;
    return {
      badge: isAmpActionStatus(status) ? ACTION_STATUS_BADGES[status] : null,
      // An action that hasn't started a step yet has no count worth showing.
      facts:
        waiting > 0
          ? [
              `${waiting} ${waiting === 1 ? "step waits" : "steps wait"} for approval`,
            ]
          : total > 0
            ? [`${total} ${plural(total, "step")}`]
            : [],
    };
  }
  if (fileBadge) return { badge: fileBadge, facts: [] };
  return NO_FACTS;
}

function plural(count: number, noun: string): string {
  return count === 1 ? noun : `${noun}s`;
}
